/**
 * useVoice Hook - Voice Recording and Transcription via Gemini STT
 *
 * Captures audio from the microphone using MediaRecorder API,
 * sends it to ai-service /transcribe (via orbit-gateway) for
 * speech-to-text powered by Gemini Flash.
 *
 * TTS playback still uses browser SpeechSynthesis API.
 */
import { useState, useRef, useCallback } from 'react';
import { getIdToken } from '../services/firebase';

const GATEWAY_URL =
  import.meta.env.VITE_ORBIT_GATEWAY_URL ||
  'https://orbit-gateway-buzbhnk2ha-uc.a.run.app';

interface UseVoiceOptions {
  onTranscription?: (text: string) => void;
  onError?: (error: string) => void;
}

interface UseVoiceReturn {
  isRecording: boolean;
  isTranscribing: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  toggleRecording: () => void;
  playText: (text: string) => Promise<void>;
  isPlaying: boolean;
  stopPlaying: () => void;
}

export function useVoice(options: UseVoiceOptions = {}): UseVoiceReturn {
  const { onTranscription, onError } = options;

  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  // ---------- helpers ----------

  /**
   * Pick a MIME type supported by both the browser and Gemini.
   * Preference order: webm/opus → webm → ogg/opus → mp4 → wav
   */
  const pickMimeType = useCallback((): string => {
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
      'audio/wav',
    ];
    for (const mime of candidates) {
      if (MediaRecorder.isTypeSupported(mime)) return mime;
    }
    return ''; // let the browser pick
  }, []);

  /**
   * Send recorded audio to the gateway /api/transcribe endpoint.
   */
  const sendAudioForTranscription = useCallback(
    async (audioBlob: Blob, mimeType: string) => {
      setIsTranscribing(true);
      try {
        const token = await getIdToken();
        if (!token) {
          onError?.('Not authenticated. Please sign in.');
          return;
        }

        const formData = new FormData();
        // Determine extension from mime
        const ext = mimeType.includes('webm')
          ? 'webm'
          : mimeType.includes('ogg')
            ? 'ogg'
            : mimeType.includes('mp4')
              ? 'mp4'
              : 'wav';
        formData.append('file', audioBlob, `recording.${ext}`);

        const response = await fetch(`${GATEWAY_URL}/api/transcribe`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        if (!response.ok) {
          const errBody = await response.json().catch(() => null);
          const msg = errBody?.message || `Transcription failed (${response.status})`;
          onError?.(msg);
          return;
        }

        const result = await response.json();

        if (result.status === 'success' && result.transcript) {
          onTranscription?.(result.transcript);
        } else if (result.status === 'success' && !result.transcript) {
          onError?.('No speech detected. Please try again.');
        } else {
          onError?.(result.message || 'Transcription failed. Please try again.');
        }
      } catch (error) {
        console.error('[useVoice] Transcription request error:', error);
        onError?.('Failed to transcribe audio. Please try again.');
      } finally {
        setIsTranscribing(false);
      }
    },
    [onTranscription, onError],
  );

  // ---------- recording ----------

  const startRecording = useCallback(async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = pickMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      chunksRef.current = [];

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        // Release mic
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;

        const actualMime = recorder.mimeType || mimeType || 'audio/webm';
        const audioBlob = new Blob(chunksRef.current, { type: actualMime });
        chunksRef.current = [];

        if (audioBlob.size < 100) {
          onError?.('Recording too short. Please try again.');
          setIsRecording(false);
          return;
        }

        // Fire-and-forget transcription (state managed inside)
        sendAudioForTranscription(audioBlob, actualMime);
        setIsRecording(false);
      };

      recorder.onerror = () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setIsRecording(false);
        setIsTranscribing(false);
        onError?.('Recording error. Please try again.');
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('[useVoice] Error starting recording:', error);
      onError?.('Microphone access denied. Please enable microphone permissions.');
    }
  }, [pickMimeType, sendAudioForTranscription, onError]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      // isRecording will be set false in onstop handler
    }
  }, [isRecording]);

  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  // ---------- TTS playback (unchanged — browser SpeechSynthesis) ----------

  const playText = useCallback(
    async (text: string) => {
      try {
        window.speechSynthesis.cancel();
        setIsPlaying(true);

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 1.0;
        utterance.pitch = 1.0;
        utterance.volume = 1.0;

        utterance.onend = () => setIsPlaying(false);
        utterance.onerror = () => {
          setIsPlaying(false);
          onError?.('Failed to play audio');
        };

        utteranceRef.current = utterance;
        window.speechSynthesis.speak(utterance);
      } catch (error) {
        console.error('[useVoice] TTS error:', error);
        setIsPlaying(false);
        onError?.(error instanceof Error ? error.message : 'Failed to generate speech');
      }
    },
    [onError],
  );

  const stopPlaying = useCallback(() => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
  }, []);

  // ---------- public API ----------

  return {
    isRecording,
    isTranscribing,
    startRecording,
    stopRecording,
    toggleRecording,
    playText,
    isPlaying,
    stopPlaying,
  };
}

export default useVoice;
